import * as XLSX from 'xlsx';
import { TARGET_FIELDS } from '../components/CommerceImportFieldMapping';

const GUIDE_SHEET_NAMES = new Set(['說明', '说明', 'Guide', 'README', 'Readme']);

const TEMPLATE_LOCALES = {
  'zh-TC': {
    guideSheet: '說明',
    guideHeaders: ['Master', '工作表', '必填欄位', '說明'],
    usageTitle: '使用方式',
    usageRows: [
      ['1', '選擇匯入對象（Master）', '', ''],
      ['2', '下載對應工作表模版或完整模版', '', ''],
      ['3', '上傳 Excel 後請在下方「選擇工作表」挑選要匯入的分頁', '', ''],
      ['4', '第一列為系統欄位鍵（請勿修改欄名）', '', ''],
      ['5', '第二列為範例資料，可刪除後填入實際資料', '', ''],
      ['6', '上傳後在「欄位對應」步驟確認自動對應', '', ''],
    ],
    sheetNames: {
      customers: '客戶主檔',
      priceCards: '價格表',
      products: '商品',
      collections: '集合',
      collectionLinks: '集合關聯',
      wpaGroupMembers: 'WPA群組成員',
      wpaGroupCustomers: 'WPA群組客戶',
      wpaContactOverrides: 'WPA聯絡人覆寫',
      wpaIdentities: 'WPA帳號',
    },
    guides: [
      { key: 'customers', name: '客戶主檔', required: 'customerNo', note: 'branchNo 預設 0000；以 customerNo+branchNo 比對更新。名稱用 name1～name3（英）；繁簡用 nameLocaleLine*ZhTc/ZhSc。車期欄位可用 Y/N、1/0。' },
      { key: 'priceCards', name: '價格表', required: 'customerNo, itemNo, unitPrice', note: '有效日期 validFrom/validTo 可選；日期區間重疊會略過。priceType 為選填。' },
      { key: 'products', name: '商品', required: 'name, productRetailerId', note: 'productRetailerId 即 Content ID；新增必填。支援區間單價 price1/2、MOQ、散裝單位等。' },
      { key: 'collections', name: '集合', required: 'name', note: 'externalCollectionKey 若已存在則更新；否則新建。' },
      { key: 'collectionLinks', name: '集合關聯', required: 'collectionKey, productRetailerId', note: '集合與商品須已存在。匯入後會移除來源檔未列出的關聯。' },
      { key: 'wpaGroupMembers', name: 'WPA 群組成員', required: 'groupName, loginId', note: 'loginId 為 PWA 登入帳號；群組與帳號須已存在。' },
      { key: 'wpaGroupCustomers', name: 'WPA 群組客戶', required: 'groupName, customerNo', note: 'branchNo 預設 0000。' },
      { key: 'wpaContactOverrides', name: 'WPA 聯絡人覆寫', required: 'loginId, roleKey', note: 'customerNos 逗號分隔，格式 customerNo/branchNo。' },
      { key: 'wpaIdentities', name: 'WPA 帳號', required: 'loginId', note: '新建時 password 必填；更新密碼需勾選「允許更新密碼」。' },
    ],
    allFilename: 'commerce-import-templates.zh-TW.xlsx',
    singleFilename: (entity) => `${entity}-import-template.zh-TW.xlsx`,
  },
  en: {
    guideSheet: 'Guide',
    guideHeaders: ['Master', 'Sheet', 'Required fields', 'Notes'],
    usageTitle: 'How to use',
    usageRows: [
      ['1', 'Select import target (master) in the app', '', ''],
      ['2', 'Download the sheet template or the full workbook', '', ''],
      ['3', 'After upload, pick the worksheet to import in "Select sheet"', '', ''],
      ['4', 'Row 1 = system field keys (do not rename headers)', '', ''],
      ['5', 'Row 2 = sample data; delete before entering real rows', '', ''],
      ['6', 'Confirm auto-mapping on the Field mapping step', '', ''],
    ],
    sheetNames: {
      customers: 'Customers',
      priceCards: 'Price Cards',
      products: 'Products',
      collections: 'Collections',
      collectionLinks: 'Collection Links',
      wpaGroupMembers: 'WPA Group Members',
      wpaGroupCustomers: 'WPA Group Customers',
      wpaContactOverrides: 'WPA Contact Overrides',
      wpaIdentities: 'WPA Accounts',
    },
    guides: [
      { key: 'customers', name: 'Customers', required: 'customerNo', note: 'branchNo defaults to 0000. Match/update by customerNo+branchNo. Use name1–name3 (EN); nameLocaleLine*ZhTc/ZhSc for TC/SC. Delivery days: Y/N.' },
      { key: 'priceCards', name: 'Price Cards', required: 'customerNo, itemNo, unitPrice', note: 'validFrom/validTo optional. Overlapping ranges are skipped. priceType optional.' },
      { key: 'products', name: 'Products', required: 'name, productRetailerId', note: 'productRetailerId = Content ID. Required for new rows. Supports price tiers, MOQ, broken-pack units, etc.' },
      { key: 'collections', name: 'Collections', required: 'name', note: 'Updates when externalCollectionKey exists.' },
      { key: 'collectionLinks', name: 'Collection Links', required: 'collectionKey, productRetailerId', note: 'Collection and product must exist. Stale links not in file are removed.' },
      { key: 'wpaGroupMembers', name: 'WPA Group Members', required: 'groupName, loginId', note: 'loginId = PWA account. Group and account must exist.' },
      { key: 'wpaGroupCustomers', name: 'WPA Group Customers', required: 'groupName, customerNo', note: 'branchNo defaults to 0000.' },
      { key: 'wpaContactOverrides', name: 'WPA Contact Overrides', required: 'loginId, roleKey', note: 'customerNos comma-separated as customerNo/branchNo.' },
      { key: 'wpaIdentities', name: 'WPA Accounts', required: 'loginId', note: 'password required on create; enable allow-password-update to change existing passwords.' },
    ],
    allFilename: 'commerce-import-templates.en.xlsx',
    singleFilename: (entity) => `${entity}-import-template.en.xlsx`,
  },
};

const SAMPLE_ROWS = {
  customers: {
    customerNo: 'C00001',
    branchNo: '0000',
    name1: 'ACME Trading Ltd',
    nameLocaleLine1ZhTc: '安美貿易有限公司',
    contactName: '陳先生',
    tel: '852-12345678',
    email: 'contact@acme.example',
    whatsappNo: '85291234567',
    salesmanName: '張業務',
    salesmanCode: 'S001',
    salesmanWhatsapp: '85298765432',
    creditLimit: '50000',
    priceType: 'A',
    billingAddressEn1: 'Room 1, 1/F, Example Building',
    billingAddressCn1: '範例大廈1樓1室',
    deliveryAddressEn1: 'Warehouse A, Kwai Chung',
    deliveryAddressCn1: '葵涌貨倉A',
    deliveryDayMon: 'Y',
    deliveryDayWed: 'Y',
    deliveryDayFri: 'Y',
    status: 'Active',
  },
  priceCards: {
    customerNo: 'C00001',
    branchNo: '0000',
    itemNo: 'SKU-001',
    unitPrice: '88.50',
    showPrice: 'true',
    validFrom: '2026-01-01',
    validTo: '2026-12-31',
    priceType: 'A',
    source: 'import',
  },
  products: {
    name: 'Sample Product A',
    nameLocaleZhTc: '範例商品甲',
    productRetailerId: 'SKU-001',
    description: 'Product description',
    brand: 'BrandX',
    price: '99.00',
    price1: '95.00',
    validFrom1: '2026-01-01',
    validTo1: '2026-06-30',
    currency: 'HKD',
    status: 'active',
    availability: 'in stock',
    unitOfMeasureLocaleEn: 'case',
    unitOfMeasureLocaleZhTc: '箱',
    unitOfBrokenPackLocaleZhTc: '件',
    qtyPerUnit: '12',
    minOrderQuantity: '1',
    oqm: 'OEM-A',
    condition: 'new',
  },
  collections: {
    name: 'Summer Promo',
    nameLocaleZhTc: '夏季推廣',
    externalCollectionKey: 'summer_2026',
    description: 'Summer collection',
    metaCatalogId: '',
  },
  collectionLinks: {
    collectionKey: 'summer_2026',
    productRetailerId: 'SKU-001',
  },
  wpaGroupMembers: {
    groupName: 'Sales Team A',
    loginId: 'user01@example.com',
  },
  wpaGroupCustomers: {
    groupName: 'Sales Team A',
    customerNo: 'C00001',
    branchNo: '0000',
  },
  wpaContactOverrides: {
    loginId: 'user01@example.com',
    roleKey: 'buyer',
    defaultCustomerNo: 'C00001',
    defaultBranchNo: '0000',
    customerNos: 'C00001/0000,C00002/0000',
    status: 'active',
  },
  wpaIdentities: {
    loginId: 'user01@example.com',
    email: 'user01@example.com',
    phoneNo: '85291234567',
    displayName: 'User One',
    password: 'ChangeMe123',
    status: 'active',
  },
};

function resolveTemplateLocale(locale) {
  if (!locale) return TEMPLATE_LOCALES['zh-TC'];
  const l = String(locale).toLowerCase();
  if (l.startsWith('en')) return TEMPLATE_LOCALES.en;
  return TEMPLATE_LOCALES['zh-TC'];
}

function headersForEntity(entityKey) {
  const fields = TARGET_FIELDS[entityKey] || [];
  return fields.map((f) => f.key);
}

function buildSheetAoa(entityKey) {
  const headers = headersForEntity(entityKey);
  const sample = SAMPLE_ROWS[entityKey] || {};
  const exampleRow = headers.map((h) => sample[h] ?? '');
  return [headers, exampleRow];
}

function buildGuideSheetAoa(localePack) {
  return [
    localePack.guideHeaders,
    ...localePack.guides.map((g) => [g.key, g.name, g.required, g.note]),
    [],
    [localePack.usageTitle, '', '', ''],
    ...localePack.usageRows,
  ];
}

function buildWorkbook(locale) {
  const pack = resolveTemplateLocale(locale);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buildGuideSheetAoa(pack)), pack.guideSheet.slice(0, 31));
  Object.keys(pack.sheetNames).forEach((entityKey) => {
    const ws = XLSX.utils.aoa_to_sheet(buildSheetAoa(entityKey));
    XLSX.utils.book_append_sheet(wb, ws, pack.sheetNames[entityKey].slice(0, 31));
  });
  return { wb, pack };
}

function triggerDownload(blob, filename) {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function workbookToBlob(wb) {
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

/** 依目前匯入對象與工作表清單，智慧預選資料分頁（略過說明頁） */
export function pickDefaultImportSheet(sheets, targetEntity, locale) {
  if (!Array.isArray(sheets) || sheets.length === 0) return '';
  const pack = resolveTemplateLocale(locale);
  const preferred = [
    pack.sheetNames[targetEntity],
    targetEntity,
    ...Object.values(pack.sheetNames),
  ].filter(Boolean);
  for (const name of preferred) {
    const hit = sheets.find((s) => s === name || String(s).toLowerCase() === String(name).toLowerCase());
    if (hit && !GUIDE_SHEET_NAMES.has(hit)) return hit;
  }
  const dataSheets = sheets.filter((s) => !GUIDE_SHEET_NAMES.has(s));
  return dataSheets[0] || sheets[0];
}

export function isGuideSheetName(name) {
  return GUIDE_SHEET_NAMES.has(name);
}

/** 下載單一 Master 模版 */
export function downloadCommerceImportTemplate(entityKey, locale, filename) {
  const { wb, pack } = buildWorkbook(locale);
  const singleWb = XLSX.utils.book_new();
  const sheetLabel = pack.sheetNames[entityKey] || entityKey;
  XLSX.utils.book_append_sheet(singleWb, XLSX.utils.aoa_to_sheet(buildSheetAoa(entityKey)), sheetLabel.slice(0, 31));
  triggerDownload(workbookToBlob(singleWb), filename || pack.singleFilename(entityKey));
}

/** 下載全部 Master 模版（多工作表 + 說明） */
export function downloadAllCommerceImportTemplates(locale, filename) {
  const { wb, pack } = buildWorkbook(locale);
  triggerDownload(workbookToBlob(wb), filename || pack.allFilename);
}

/** Node：寫入 public 靜態檔 */
export function writeAllCommerceImportTemplatesToFile(filePath, locale) {
  const { wb } = buildWorkbook(locale);
  XLSX.writeFile(wb, filePath);
}

export { TEMPLATE_LOCALES, resolveTemplateLocale };
